import React from 'react';
import PropTypes from 'prop-types';
import { v4 as uuid } from 'uuid';
import './Dialog.scss';
import ReactModal from 'react-modal';
import Button from '../Button/Button';
import Icon from '../Icon/Icon';
import { createClassList } from '../../utils/dom/class';

const Dialog = ({
  icon,
  title,
  ariaDescribedBy,
  children,
  open,
  className,
  buttons,
  onRequestClose,
  parentSelector,
  showCloseIcon,
  role,
  classes,
}) => {
  const dialogTitleId = uuid();

  const standardClasses = className
    ? ['axiom-dialog', 'dialog', className]
    : ['axiom-dialog', 'dialog'];
  // Note: className prop included to avoid breaking change

  const classGroup = createClassList({
    default: standardClasses,
    custom: classes,
    required: [],
  });

  return (
    <ReactModal
      isOpen={open}
      overlayClassName='dialog--overlay'
      bodyOpenClassName='ReactDialog__Body--open'
      className={classGroup}
      onRequestClose={onRequestClose}
      contentLabel={title}
      aria={{
        live: 'assertive',
        modal: true,
        labelledby: dialogTitleId,
        describedby: ariaDescribedBy,
      }}
      closeTimeoutMS={300}
      ariaHideApp={false}
      parentSelector={parentSelector}
      role={role}
    >
      <section>
        <div className='axiom-dialog--body dialog--body'>
          {(buttons.length === 0 || showCloseIcon) && (
            <button
              type='button'
              onClick={onRequestClose}
              className='axiom-dialog--body--close dialog--body--close'
              aria-label='Close Dialog'
            >
              <Icon name='close' />
            </button>
          )}
          {icon && <Icon name={icon} />}
          <div className='axiom-dialog--body--content dialog--body--content'>
            <header>
              <h1
                id={dialogTitleId}
                className='axiom-dialog--body--content--title dialog--body--content--title'
              >
                {title}
              </h1>
            </header>
            {children}
          </div>
        </div>
        {buttons.length > 0 && (
          <>
            <footer
              className='axiom-dialog--actions dialog--actions'
              data-testid='dialog-actions'
            >
              {buttons.map(
                (
                  {
                    text, // TODO - Figure out why this is text instead of label to match ButtonProps
                    styleVariant,
                    onClick,
                    attributes,
                    disabled,
                    loading,
                    icon: buttonIconProperty, // scope conflict
                    inputRef,
                    iconPosition,
                    iconOnly,
                  },
                  index
                ) => (
                  <div key={`${text + index}`}>
                    <Button
                      label={text}
                      onClick={onClick}
                      styleVariant={styleVariant || 'secondary'}
                      attributes={attributes}
                      disabled={disabled}
                      loading={loading}
                      icon={buttonIconProperty}
                      inputRef={inputRef}
                      iconPosition={iconPosition}
                      iconOnly={iconOnly}
                      classes='axiom-dialog--actions-button'
                    />
                  </div>
                )
              )}
            </footer>
          </>
        )}
      </section>
    </ReactModal>
  );
};

Dialog.propTypes = {
  icon: PropTypes.string,
  title: PropTypes.string.isRequired,
  ariaDescribedBy: PropTypes.string,
  children: PropTypes.node.isRequired,
  open: PropTypes.bool.isRequired,
  className: PropTypes.string,
  buttons: PropTypes.arrayOf(
    PropTypes.shape({
      styleVariant: PropTypes.oneOf([
        'primary',
        'secondary',
        'tertiary',
      ]),
      text: PropTypes.string.isRequired,
      onClick: PropTypes.func.isRequired,
      attributes: PropTypes.objectOf(PropTypes.any),
      disabled: PropTypes.bool,
      loading: PropTypes.bool,
      icon: PropTypes.string,
      inputRef: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
      ]),
      iconPosition: PropTypes.oneOf(['left', 'right']),
      iconOnly: PropTypes.bool,
    })
  ),
  onRequestClose: PropTypes.func.isRequired,
  parentSelector: PropTypes.func,
  showCloseIcon: PropTypes.bool,
  role: PropTypes.string,
  classes: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
};

Dialog.defaultProps = {
  icon: undefined,
  ariaDescribedBy: undefined,
  className: undefined,
  buttons: [],
  parentSelector: undefined,
  showCloseIcon: false,
  role: 'dialog',
  classes: [],
};

export default Dialog;
